/**
 * AccountSettingsPage — monitored account CRUD (replaces Unit 1 stub).
 *
 * Orchestrates:
 *   - useAccounts: read account list
 *   - useCreateAccount / useUpdateAccount / useDeleteAccount: mutations
 *   - AccountModal: create and edit form (conditional)
 *   - AccountRow: table rows with Edit/Delete actions
 *
 * Error handling: Pattern 8.2 for query errors, Pattern 8.3 for mutation errors.
 */
import { useState } from 'react';
import { useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount } from '@/api/accountsApi';
import type { AccountApiError } from '@/api/accountsApi';
import type { MonitoredAccount, MonitoredAccountRequest } from '@/types';
import { AccountRow } from '@/components/AccountRow';
import { AccountModal } from '@/components/AccountModal';
import styles from './AccountSettingsPage.module.css';

export function AccountSettingsPage() {
  const [modalState, setModalState] = useState<
    { mode: 'add' } | { mode: 'edit'; account: MonitoredAccount } | null
  >(null);

  const { data: accounts, error: accountsError, isLoading, refetch } = useAccounts();

  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();

  const handleAdd = () => setModalState({ mode: 'add' });
  const handleEdit = (account: MonitoredAccount) => setModalState({ mode: 'edit', account });
  const handleClose = () => {
    setModalState(null);
    createAccount.reset();
    updateAccount.reset();
  };

  const handleSubmit = (request: MonitoredAccountRequest) => {
    if (modalState?.mode === 'edit') {
      updateAccount.mutate(
        { id: modalState.account.id, request },
        { onSuccess: () => handleClose() },
      );
    } else {
      createAccount.mutate(request, { onSuccess: () => handleClose() });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this account? Collection for this account will stop.')) {
      deleteAccount.mutate(id);
    }
  };

  const submitError =
    modalState?.mode === 'edit' ? (updateAccount.error as AccountApiError | null) :
    (createAccount.error as AccountApiError | null);

  const isSubmitting =
    modalState?.mode === 'edit' ? updateAccount.isPending : createAccount.isPending;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Account Settings</h1>
          <p className={styles.subtitle}>
            Manage the AWS accounts that System Monitor collects data from.
          </p>
        </div>
        <button
          type="button"
          className={styles.addButton}
          onClick={handleAdd}
          aria-label="Add a new monitored account"
        >
          + Add Account
        </button>
      </header>

      {deleteAccount.isError && (
        <div role="alert" className={styles.errorBanner}>
          Failed to delete account: {(deleteAccount.error as Error).message}
        </div>
      )}

      {accountsError && !accounts && (
        <div role="alert" className={styles.errorCard}>
          <p>Unable to load accounts. {(accountsError as Error).message}</p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {isLoading && !accounts && (
        <div aria-busy="true" className={styles.loading}>
          Loading accounts…
        </div>
      )}

      {accounts && accounts.length === 0 && (
        <div className={styles.empty} role="status">
          <p>No accounts configured. Add an account to start monitoring.</p>
        </div>
      )}

      {accounts && accounts.length > 0 && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <caption className={styles.srOnly}>Monitored AWS accounts</caption>
            <thead>
              <tr className={styles.thead}>
                <th scope="col" className={styles.th}>Account</th>
                <th scope="col" className={styles.th}>Account ID</th>
                <th scope="col" className={styles.th}>Regions</th>
                <th scope="col" className={styles.th}>Last Updated</th>
                <th scope="col" className={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <AccountRow
                  key={account.id}
                  account={account}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isDeletePending={deleteAccount.isPending && deleteAccount.variables === account.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalState && (
        <AccountModal
          account={modalState.mode === 'edit' ? modalState.account : undefined}
          onClose={handleClose}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitError={submitError}
        />
      )}
    </div>
  );
}
